import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./PortalLoginPage.css";

/**
 * PortalLoginPage
 * - Microsoft sign-in (recommended)
 * - Google sign-in (fallback)
 *
 * IMPORTANT (v1 locked access model):
 * - Clinic Admins request access AFTER signing in (includes solo physicians managing their own clinic).
 * - Providers do NOT request access. Providers are invited by their Clinic Admin after clinic approval.
 *
 * Auth wiring:
 * - Uses SWA auth endpoints: /.auth/login/aad and /.auth/login/google
 * - After login, redirect to /portal (role router) OR returnTo if present
 * - /portal calls POST /api/portal-auth-bootstrap to upsert dbo.User + fetch roles
 */

type AuthMeResponse = {
  clientPrincipal: null | {
    identityProvider: string;
    userDetails: string;
    userRoles?: string[];
    claims?: Array<{ typ: string; val: string }>;
  };
};

// SVG Icons
const MicrosoftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" fill="none">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const BuildingIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M12 14h.01M16 10h.01M16 14h.01M8 10h.01M8 14h.01" />
  </svg>
);

const UserIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

/**
 * Only allow internal, same-app paths.
 * - must start with "/"
 * - must NOT start with "//"
 * - must NOT contain a scheme like "http:"
 */
function sanitizeReturnTo(value: string | null): string | null {
  const v = (value || "").trim();
  if (!v) return null;
  if (!v.startsWith("/")) return null;
  if (v.startsWith("//")) return null;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(v)) return null; // blocks "http:", "javascript:", etc.
  return v;
}

const PortalLoginPage: React.FC = () => {
  const [authChecking, setAuthChecking] = useState(true);
  const [authEmail, setAuthEmail] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  const origin = useMemo(() => window.location.origin, []);

  // ✅ Support returnTo flow (e.g., /portal/login?returnTo=/portal/accept-invite?token=...)
  const returnTo = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return sanitizeReturnTo(params.get("returnTo"));
    } catch {
      return null;
    }
  }, []);

  /**
   * ✅ LOCKED v1 pattern:
   * Always redirect back to /portal, carrying returnTo as a query param.
   * Then PortalHomePage will honor returnTo (after confirming session).
   */
  const postLoginRedirect = useMemo(() => {
    const qs = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : "";
    return `${origin}/portal${qs}`;
  }, [origin, returnTo]);

  const onMicrosoftSignIn = () => {
    window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(
      postLoginRedirect
    )}`;
  };

  const onGoogleSignIn = () => {
    window.location.href = `/.auth/login/google?post_login_redirect_uri=${encodeURIComponent(
      postLoginRedirect
    )}`;
  };

  useEffect(() => {
    const run = async () => {
      try {
        setAuthChecking(true);
        setAuthError(null);

        const meRes = await fetch("/.auth/me", { credentials: "include" });
        const meJson = (await meRes.json()) as AuthMeResponse;

        if (!meJson.clientPrincipal) {
          setAuthChecking(false);
          return;
        }

        setAuthEmail(meJson.clientPrincipal.userDetails || null);

        // ✅ If already signed in, go to /portal with returnTo so invite acceptance can complete.
        window.location.href = `${window.location.origin}/portal${
          returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ""
        }`;
      } catch (e: any) {
        setAuthError(e?.message || "Authentication check failed.");
        setAuthChecking(false);
      }
    };

    run();
  }, [returnTo]);

  return (
    <div className="pl-page">
      <div className="pl-shell">
        {/* Top bar */}
        <div className="pl-topbar">
          <Link
            to="/public"
            className="pl-brand"
            aria-label="Back to Public Site"
          >
            <span className="pl-brandLogo" aria-hidden>
              <img src="/logo-1.png" alt="" className="pl-brandImg" />
            </span>
            <span className="pl-brandName">AcceptingMD</span>
          </Link>

          <Link to="/public" className="pl-backLink">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to site
          </Link>
        </div>

        {/* Main content */}
        <div className="pl-content">
          <section className="pl-card" aria-label="Portal sign-in">
            <div className="pl-cardHeader">
              <h1 className="pl-cardTitle">Provider Portal</h1>
              <p className="pl-cardSub">
                Sign in to access professional tools and manage your clinic.
              </p>
            </div>

            {authError && (
              <div className="pl-alert pl-alert--error" role="alert">
                <div className="pl-alertTitle">Sign-in error</div>
                <div>{authError}</div>
              </div>
            )}

            {authChecking ? (
              <div className="pl-loading">
                <div className="pl-spinner" />
                <span>
                  Checking session{authEmail ? ` (${authEmail})` : ""}...
                </span>
              </div>
            ) : (
              <>
                {/* SIGN-IN (unchanged) */}
                <div className="pl-actions">
                  <button
                    type="button"
                    onClick={onMicrosoftSignIn}
                    className="pl-btn pl-btn--primary"
                  >
                    <MicrosoftIcon />
                    <span>Sign in with Microsoft</span>
                    <span className="pl-btnTag">Recommended</span>
                  </button>

                  <button
                    type="button"
                    onClick={onGoogleSignIn}
                    className="pl-btn pl-btn--secondary"
                  >
                    <GoogleIcon />
                    <span>Sign in with Google</span>
                  </button>
                </div>

                {/* SIGN-UP (separate UX section) */}
                <div className="pl-footHint" style={{ marginTop: 18 }}>
                  <b>New to AcceptingMD?</b> Sign up here
                </div>

                <div className="pl-infoGrid" aria-label="Sign-up options">
                  {/* Clinic Admin card is clickable (routes to new explainer page) */}
                  <Link
                    to="/portal/clinic-admin-signup"
                    className="pl-infoCard"
                    style={{ textDecoration: "none", color: "inherit" }}
                    aria-label="Sign up as a Clinic Admin"
                  >
                    <div className="pl-infoIcon">
                      <BuildingIcon />
                    </div>
                    <div className="pl-infoCardTitle">Clinic Admins</div>
                    <div className="pl-infoCardText">
                      Sign up to onboard a clinic and manage provider access. You’ll sign in first, then submit a Clinic Admin request for approval.
                    </div>
                  </Link>

                  {/* Providers card stays non-clickable (more detailed text) */}
                  <div className="pl-infoCard" aria-label="Provider sign-up info">
                    <div className="pl-infoIcon">
                      <UserIcon />
                    </div>
                    <div className="pl-infoCardTitle">Providers</div>
                    <div className="pl-infoCardText">
                      Providers do not sign up directly. You’ll be invited by a Clinic Admin after the clinic is approved.
                      <br />
                      <br />
                      If you need to be the Clinic Admin, sign up as a <b>Clinic Admin</b> first, then invite yourself as a provider so you can have both roles. Later, your clinic can add additional Clinic Admins.
                    </div>
                  </div>
                </div>

                {/* Warning */}
                <div className="pl-warn">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span>
                    Do not submit any patient information in the portal access
                    flow.
                  </span>
                </div>

                {/* Footer links */}
                <div className="pl-footerLinks">
                  <div className="pl-footHint">
                    Looking for care?{" "}
                    <Link to="/public/find-family-doctor" className="pl-link">
                      Find a Family Doctor
                    </Link>
                  </div>
                  <div className="pl-footHint">
                    Questions?{" "}
                    <Link to="/public/contact" className="pl-link">
                      Contact Us
                    </Link>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>

        <div className="pl-footer">
          © {new Date().getFullYear()} AcceptingMD. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default PortalLoginPage;
